<template>
  <div class="facial-devices">
    <v-container grid-list-md>
      <v-layout row wrap>
        <v-flex xs12>
          <h1 class="display-1 font-weight-bold mb-2">Control de Acceso</h1>
          <p class="subtitle grey--text">Faciales del edificio: estatus en tiempo real y accesos registrados</p>
        </v-flex>
      </v-layout>

      <v-layout row wrap v-if="generalError" class="mb-3">
        <v-flex xs12>
          <v-alert type="error" dismissible :value="true" @input="generalError = ''">{{ generalError }}</v-alert>
        </v-flex>
      </v-layout>

      <!-- Resumen y middleware -->
      <v-layout row wrap class="mb-3" align-center>
        <v-flex xs12 md7>
          <v-chip color="blue-grey" text-color="white"><v-avatar class="blue-grey darken-3">{{ devices.length }}</v-avatar>Faciales</v-chip>
          <v-chip color="success" text-color="white"><v-avatar class="green darken-3">{{ countByStatus('ONLINE') }}</v-avatar>En línea</v-chip>
          <v-chip color="error" text-color="white"><v-avatar class="red darken-3">{{ countByStatus('OFFLINE') }}</v-avatar>Sin conexión</v-chip>
          <v-chip color="grey" text-color="white"><v-avatar class="grey darken-3">{{ countByStatus('UNKNOWN') }}</v-avatar>Sin reporte</v-chip>
        </v-flex>
        <v-flex xs12 md5 class="text-md-right">
          <v-chip :color="middleware.connected ? 'success' : 'grey'" text-color="white" @click="openKeyDialog">
            <v-icon left small>{{ middleware.connected ? 'cloud_done' : 'cloud_off' }}</v-icon>
            Middleware {{ middlewareLabel }}
          </v-chip>
          <v-btn flat icon :loading="loadingDevices" title="Actualizar" @click="refreshAll"><v-icon>refresh</v-icon></v-btn>
          <v-btn color="primary" @click="openFormDialog()"><v-icon left>add</v-icon>Agregar facial</v-btn>
        </v-flex>
      </v-layout>

      <!-- Maqueta de faciales -->
      <v-layout row wrap>
        <v-flex xs12 v-if="!devices.length && !loadingDevices">
          <v-card class="elevation-1 pa-4 text-xs-center grey--text">
            Aún no hay faciales registrados. Usa "Agregar facial" para dar de alta el primero.
          </v-card>
        </v-flex>
        <v-flex xs6 sm4 md3 lg2 v-for="device in devices" :key="device.id">
          <v-card
            class="device-card text-xs-center pa-2"
            :class="{ 'device-inactive': !device.is_active }"
            hover
            @click="openDevice(device)"
          >
            <facial-illustration class="device-svg" :color="statusHex(device)" />
            <div class="subheading font-weight-bold mt-1 device-name">{{ device.name }}</div>
            <div class="caption grey--text device-location">{{ device.location || 'Sin ubicación' }}</div>
            <v-chip small :color="statusColor(device)" text-color="white">{{ statusLabel(device) }}</v-chip>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Detalle del facial -->
    <v-dialog v-model="detailDialog" max-width="760px" scrollable>
      <v-card v-if="selected" class="detail-card">
        <div class="detail-header" :style="{ background: headerGradient(selected) }">
          <v-btn icon dark class="detail-close" @click="detailDialog = false"><v-icon>close</v-icon></v-btn>
          <div class="detail-header-content">
            <facial-illustration class="detail-illustration" :color="statusHex(selected)" />
            <div class="detail-title">
              <div class="headline white--text font-weight-bold">{{ selected.name }}</div>
              <div class="white--text detail-location">
                <v-icon small dark>place</v-icon> {{ selected.location || 'Sin ubicación' }}
              </div>
              <v-chip small color="white" :text-color="statusColor(selected) === 'grey' ? 'grey darken-2' : statusColor(selected)">
                <v-icon left small :color="statusColor(selected)">{{ statusIcon(selected) }}</v-icon>
                {{ statusLabel(selected) }}
              </v-chip>
              <div class="white--text detail-hint">{{ statusHint(selected) }}</div>
            </div>
          </div>
        </div>

        <v-tabs v-model="detailTab" grow color="white" slider-color="primary">
          <v-tab>Estatus</v-tab>
          <v-tab>Accesos</v-tab>
          <v-tab-item>
            <v-card-text class="detail-body">
              <div class="section-title">Información del equipo</div>
              <v-layout row wrap>
                <v-flex xs12 sm6 v-for="tile in infoTiles" :key="tile.label">
                  <div class="info-tile">
                    <v-icon class="info-icon" color="blue-grey lighten-1">{{ tile.icon }}</v-icon>
                    <div class="info-text">
                      <div class="caption grey--text">{{ tile.label }}</div>
                      <div class="body-2 info-value">{{ tile.value }}</div>
                    </div>
                  </div>
                </v-flex>
              </v-layout>
              <div v-if="selected.notes" class="notes-box">
                <v-icon small class="mr-1">notes</v-icon>{{ selected.notes }}
              </div>
              <v-alert :value="!selected.is_active" type="info" outline class="mt-3">
                Este facial está inactivo: el middleware no lo monitorea.
              </v-alert>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text class="detail-body">
              <div class="access-summary" v-if="deviceEvents.items.length">
                <v-chip small color="success" text-color="white"><v-icon left small>check_circle</v-icon>{{ grantedCount }} permitidos</v-chip>
                <v-chip small color="error" text-color="white"><v-icon left small>cancel</v-icon>{{ deniedCount }} denegados</v-chip>
                <span class="caption grey--text">de los últimos {{ deviceEvents.items.length }} cargados</span>
              </div>
              <div v-if="deviceEvents.loading && !deviceEvents.items.length" class="text-xs-center pa-4">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
              <div v-else-if="!deviceEvents.items.length" class="empty-state">
                <v-icon size="48" color="grey lighten-1">history</v-icon>
                <div class="grey--text">Este facial aún no tiene accesos registrados</div>
              </div>
              <v-list v-else two-line class="access-list">
                <template v-for="(event, index) in deviceEvents.items">
                  <v-list-tile :key="'e' + event.id" class="access-item">
                    <v-list-tile-avatar>
                      <v-icon large :color="event.result === 'GRANTED' ? 'success' : 'error'">
                        {{ event.result === 'GRANTED' ? 'check_circle' : 'cancel' }}
                      </v-icon>
                    </v-list-tile-avatar>
                    <v-list-tile-content>
                      <v-list-tile-title class="font-weight-medium">{{ event.person_name || 'Persona no identificada' }}</v-list-tile-title>
                      <v-list-tile-sub-title>{{ eventSubtitle(event) }}</v-list-tile-sub-title>
                    </v-list-tile-content>
                    <v-list-tile-action class="access-time">
                      <span class="caption">{{ formatTime(event.occurred_at) }}</span>
                      <span class="caption grey--text">{{ formatDay(event.occurred_at) }}</span>
                    </v-list-tile-action>
                  </v-list-tile>
                  <v-divider v-if="index < deviceEvents.items.length - 1" :key="'d' + event.id" inset></v-divider>
                </template>
              </v-list>
              <div class="text-xs-center pa-2" v-if="deviceEvents.items.length < deviceEvents.count">
                <v-btn flat color="primary" :loading="deviceEvents.loading" @click="loadDeviceEvents(false)">
                  Cargar más ({{ deviceEvents.items.length }} de {{ deviceEvents.count }})
                </v-btn>
              </div>
            </v-card-text>
          </v-tab-item>
        </v-tabs>
        <v-divider></v-divider>
        <v-card-actions class="detail-actions">
          <v-btn flat color="error" @click="openDeleteDialog(selected)"><v-icon left>delete</v-icon>Eliminar</v-btn>
          <v-spacer></v-spacer>
          <v-btn flat color="primary" @click="openFormDialog(selected)"><v-icon left>edit</v-icon>Editar</v-btn>
          <v-btn flat @click="detailDialog = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Alta / edición -->
    <v-dialog v-model="formDialog" max-width="560px" persistent>
      <v-card>
        <v-card-title><span class="headline">{{ editing ? 'Editar facial' : 'Agregar facial' }}</span></v-card-title>
        <v-card-text>
          <v-form ref="deviceForm" v-model="formValid">
            <v-text-field v-model="form.name" label="Nombre *" prepend-icon="sensor_door" :rules="requiredRules" :error-messages="fieldErrors('name')"></v-text-field>
            <v-text-field v-model="form.location" label="Ubicación (zona / piso)" prepend-icon="place" :error-messages="fieldErrors('location')"></v-text-field>
            <v-text-field v-model="form.identifier" label="Identificador (serie o MAC) *" prepend-icon="qr_code" hint="Es el que usa el middleware para reportar este facial" persistent-hint :rules="requiredRules" :error-messages="fieldErrors('identifier')"></v-text-field>
            <v-layout row wrap>
              <v-flex xs12 sm6><v-text-field v-model="form.model" label="Modelo" prepend-icon="devices" :error-messages="fieldErrors('model')"></v-text-field></v-flex>
              <v-flex xs12 sm6><v-text-field v-model="form.ip_address" label="IP" prepend-icon="lan" :error-messages="fieldErrors('ip_address')"></v-text-field></v-flex>
            </v-layout>
            <v-textarea v-model="form.notes" label="Notas" prepend-icon="notes" rows="2" :error-messages="fieldErrors('notes')"></v-textarea>
            <v-switch v-model="form.is_active" label="Activo (monitoreado por el middleware)"></v-switch>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="saving" @click="closeFormDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveDevice">{{ editing ? 'Actualizar' : 'Crear' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmar eliminación -->
    <v-dialog v-model="deleteDialog" max-width="440px">
      <v-card>
        <v-card-title class="headline">Eliminar facial</v-card-title>
        <v-card-text>
          ¿Eliminar <strong>{{ toDelete ? toDelete.name : '' }}</strong>? También se borrará su historial de accesos. Si solo quieres dejar de monitorearlo, desactívalo en su lugar.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="saving" @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="saving" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Credencial del middleware -->
    <v-dialog v-model="keyDialog" max-width="560px">
      <v-card>
        <v-card-title class="headline">Credencial del middleware</v-card-title>
        <v-card-text>
          <p>
            El middleware instalado en el servidor del edificio se autentica con una API key en el header
            <code>X-Api-Key</code>. Solo existe una activa: al generar una nueva, la anterior deja de funcionar.
          </p>
          <v-alert :value="true" :type="middleware.active ? 'info' : 'warning'" outline class="mb-3">
            <template v-if="middleware.active">
              Clave activa <strong>{{ middleware.prefix }}…</strong> · creada {{ formatDate(middleware.created_at) }} ·
              último uso {{ formatDate(middleware.last_used_at) || 'nunca' }}
            </template>
            <template v-else>No hay una credencial activa.</template>
          </v-alert>
          <div v-if="newKey">
            <v-alert :value="true" type="success" outline>Copia la clave ahora: no se volverá a mostrar.</v-alert>
            <v-text-field :value="newKey" readonly label="API key" append-icon="content_copy" @click:append="copyKey" @focus="$event.target.select()"></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn v-if="middleware.active" flat color="error" :loading="savingKey" @click="revokeKey">Revocar</v-btn>
          <v-spacer></v-spacer>
          <v-btn flat @click="closeKeyDialog">Cerrar</v-btn>
          <v-btn color="primary" :loading="savingKey" @click="generateKey">{{ middleware.active ? 'Generar nueva' : 'Generar' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import '@/plugins/axios'
import axios from 'axios'
import store from '@/store'
import FacialIllustration from '@/components/FacialIllustration.vue'

const API_BASE_URL = 'https://empresa-service.intrare.app'
const POLL_MS = 30000
const PAGE = 50

export default {
  name: 'FacialDevices',
  components: { FacialIllustration },
  data() {
    return {
      devices: [],
      loadingDevices: false,
      middleware: { active: false, connected: false, prefix: '', created_at: null, last_used_at: null },
      generalError: '',
      pollTimer: null,

      detailDialog: false,
      detailTab: 0,
      selectedId: null,
      deviceEvents: { items: [], count: 0, loading: false },

      formDialog: false,
      formValid: true,
      editing: null,
      saving: false,
      errores: {},
      form: this.emptyForm(),
      requiredRules: [value => !!value || 'Este campo es requerido'],

      deleteDialog: false,
      toDelete: null,

      keyDialog: false,
      savingKey: false,
      newKey: ''
    }
  },
  computed: {
    selected() {
      return this.devices.find(device => device.id === this.selectedId) || null
    },
    middlewareLabel() {
      if (!this.middleware.active) return 'sin credencial'
      return this.middleware.connected ? 'conectado' : 'sin señal'
    },
    grantedCount() {
      return this.deviceEvents.items.filter(event => event.result === 'GRANTED').length
    },
    deniedCount() {
      return this.deviceEvents.items.filter(event => event.result === 'DENIED').length
    },
    infoTiles() {
      const device = this.selected
      if (!device) return []
      const tiles = [
        { icon: 'sync', label: 'Último reporte del middleware', value: this.formatDate(device.last_report_at) || 'Nunca ha reportado' },
        { icon: 'wifi', label: 'Última vez en línea', value: this.formatDate(device.last_online_at) || '—' },
        { icon: 'event_available', label: 'Último acceso', value: this.formatDate(device.last_event_at) || '—' },
        { icon: 'qr_code', label: 'Identificador (serie / MAC)', value: device.identifier },
        { icon: 'memory', label: 'Modelo', value: device.model || '—' },
        { icon: 'lan', label: 'Dirección IP', value: device.ip_address || 'Sin IP' },
        { icon: 'system_update', label: 'Firmware', value: device.firmware_version || 'Sin dato' }
      ]
      const details = device.status_details || {}
      Object.keys(details).forEach(key => {
        tiles.push({ icon: 'info', label: key, value: String(details[key]) })
      })
      return tiles
    }
  },
  watch: {
    detailDialog(open) {
      if (!open) this.selectedId = null
    },
    detailTab(tab) {
      if (tab === 1 && this.selected) this.loadDeviceEvents(true)
    }
  },
  mounted() {
    this.refreshAll()
    this.pollTimer = setInterval(this.pollRefresh, POLL_MS)
  },
  beforeDestroy() {
    clearInterval(this.pollTimer)
  },
  methods: {
    emptyForm() {
      return { name: '', location: '', identifier: '', model: 'Fanvil i67', ip_address: '', notes: '', is_active: true }
    },
    authHeaders() {
      return { 'Content-Type': 'application/json', 'authorization': 'Token ' + store.getters.getToken }
    },
    // El color del snackbar es un estado global que otras pantallas dejan en 'error', así que se fija siempre
    notify(message, color) {
      this.$store.commit('snackbar_change_color', color || 'success')
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_activate_true')
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      if (!data) return fallback
      return data.error || data.detail || fallback
    },
    fieldErrors(field) {
      const value = this.errores[field]
      if (!value) return []
      return Array.isArray(value) ? value : [value]
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('es-MX') : ''
    },
    formatTime(value) {
      return value ? new Date(value).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''
    },
    formatDay(value) {
      return value ? new Date(value).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : ''
    },
    timeAgo(value) {
      const seconds = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 1000))
      if (seconds < 60) return 'hace unos segundos'
      const minutes = Math.round(seconds / 60)
      if (minutes < 60) return `hace ${minutes} min`
      const hours = Math.round(minutes / 60)
      if (hours < 48) return `hace ${hours} h`
      return `hace ${Math.round(hours / 24)} días`
    },
    eventSubtitle(event) {
      const parts = [event.company_name, event.space_code ? `Cajón ${event.space_code}` : '', event.method_display]
      if (event.result === 'DENIED' && event.deny_reason) parts.push(event.deny_reason)
      return parts.filter(Boolean).join(' · ')
    },

    refreshAll() {
      this.fetchDevices()
      this.fetchMiddleware()
    },
    pollRefresh() {
      if (document.hidden) return
      this.fetchDevices(true)
      this.fetchMiddleware()
    },

    // --- Faciales -----------------------------------------------------------
    fetchDevices(silent) {
      if (!silent) this.loadingDevices = true
      return axios.get(`${API_BASE_URL}/Parking/building-access/devices/`, { headers: this.authHeaders() })
        .then(response => { this.devices = response.data })
        .catch(error => {
          if (!silent) this.generalError = this.errorMessage(error, 'Error al cargar los faciales')
        })
        .finally(() => { this.loadingDevices = false })
    },
    statusOf(device) {
      return device.is_active ? device.status : 'INACTIVE'
    },
    countByStatus(status) {
      return this.devices.filter(device => device.is_active && device.status === status).length
    },
    statusLabel(device) {
      return { ONLINE: 'En línea', OFFLINE: 'Sin conexión', UNKNOWN: 'Sin reporte', INACTIVE: 'Inactivo' }[this.statusOf(device)]
    },
    statusColor(device) {
      return { ONLINE: 'success', OFFLINE: 'error', UNKNOWN: 'grey', INACTIVE: 'grey' }[this.statusOf(device)]
    },
    statusIcon(device) {
      return { ONLINE: 'check_circle', OFFLINE: 'error', UNKNOWN: 'help', INACTIVE: 'pause_circle_filled' }[this.statusOf(device)]
    },
    statusHex(device) {
      return { ONLINE: '#43a047', OFFLINE: '#e53935', UNKNOWN: '#9e9e9e', INACTIVE: '#616161' }[this.statusOf(device)]
    },
    headerGradient(device) {
      const gradients = {
        ONLINE: 'linear-gradient(135deg, #2e7d32, #66bb6a)',
        OFFLINE: 'linear-gradient(135deg, #b71c1c, #ef5350)',
        UNKNOWN: 'linear-gradient(135deg, #455a64, #90a4ae)',
        INACTIVE: 'linear-gradient(135deg, #37474f, #78909c)'
      }
      return gradients[this.statusOf(device)]
    },
    statusHint(device) {
      const status = this.statusOf(device)
      if (status === 'ONLINE') return `Reportando con normalidad · ${this.timeAgo(device.last_report_at)}`
      if (status === 'OFFLINE') {
        return device.last_report_at
          ? `Sin respuesta desde ${this.timeAgo(device.last_report_at)}`
          : 'El middleware lo reporta sin conexión'
      }
      if (status === 'UNKNOWN') return 'El middleware aún no ha reportado este facial'
      return 'Facial desactivado'
    },
    openDevice(device) {
      this.selectedId = device.id
      this.detailTab = 0
      this.deviceEvents = { items: [], count: 0, loading: false }
      this.detailDialog = true
    },

    openFormDialog(device) {
      this.errores = {}
      this.editing = device || null
      this.form = device
        ? {
          name: device.name,
          location: device.location,
          identifier: device.identifier,
          model: device.model,
          ip_address: device.ip_address || '',
          notes: device.notes,
          is_active: device.is_active
        }
        : this.emptyForm()
      this.formDialog = true
    },
    closeFormDialog() {
      if (this.saving) return
      this.formDialog = false
      this.editing = null
      this.errores = {}
      if (this.$refs.deviceForm) this.$refs.deviceForm.resetValidation()
    },
    saveDevice() {
      if (!this.$refs.deviceForm.validate()) return
      this.saving = true
      this.errores = {}
      const data = Object.assign({}, this.form, { ip_address: this.form.ip_address || null })
      const url = this.editing
        ? `${API_BASE_URL}/Parking/building-access/devices/${this.editing.id}/`
        : `${API_BASE_URL}/Parking/building-access/devices/`
      // Éxito y error van separados: un fallo al refrescar la pantalla no debe mostrarse como fallo al guardar
      axios({ method: this.editing ? 'patch' : 'post', url, headers: this.authHeaders(), data })
        .then(
          () => {
            this.saving = false
            this.closeFormDialog()
            this.notify('Facial guardado')
            this.fetchDevices(true)
          },
          error => {
            this.saving = false
            if (error.response && error.response.status === 400 && typeof error.response.data === 'object') {
              this.errores = error.response.data
            } else {
              this.formDialog = false
              this.generalError = this.errorMessage(error, 'Error al guardar el facial')
            }
          }
        )
    },
    openDeleteDialog(device) {
      this.toDelete = device
      this.deleteDialog = true
    },
    confirmDelete() {
      this.saving = true
      axios.delete(`${API_BASE_URL}/Parking/building-access/devices/${this.toDelete.id}/`, { headers: this.authHeaders() })
        .then(
          () => {
            this.saving = false
            this.deleteDialog = false
            this.detailDialog = false
            this.toDelete = null
            this.notify('Facial eliminado')
            this.fetchDevices(true)
          },
          error => {
            this.saving = false
            this.deleteDialog = false
            this.generalError = this.errorMessage(error, 'Error al eliminar el facial')
          }
        )
    },

    // --- Accesos del facial -----------------------------------------------------
    loadDeviceEvents(reset) {
      if (!this.selected) return
      const offset = reset ? 0 : this.deviceEvents.items.length
      this.deviceEvents.loading = true
      axios.get(`${API_BASE_URL}/Parking/building-access/events/`, {
        headers: this.authHeaders(),
        params: { limit: PAGE, offset, device: this.selected.id }
      })
        .then(response => {
          this.deviceEvents.count = response.data.count
          this.deviceEvents.items = reset ? response.data.results : this.deviceEvents.items.concat(response.data.results)
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'Error al cargar los accesos del facial') })
        .finally(() => { this.deviceEvents.loading = false })
    },

    // --- Middleware ------------------------------------------------------------
    fetchMiddleware() {
      return axios.get(`${API_BASE_URL}/Parking/building-access/middleware-key/`, { headers: this.authHeaders() })
        .then(response => { this.middleware = response.data })
        .catch(() => {})
    },
    openKeyDialog() {
      this.newKey = ''
      this.keyDialog = true
    },
    closeKeyDialog() {
      this.keyDialog = false
      this.newKey = ''
    },
    generateKey() {
      if (this.middleware.active && !window.confirm('La credencial actual dejará de funcionar. ¿Generar una nueva?')) return
      this.savingKey = true
      axios.post(`${API_BASE_URL}/Parking/building-access/middleware-key/`, {}, { headers: this.authHeaders() })
        .then(response => {
          this.newKey = response.data.api_key
          this.middleware = response.data
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible generar la credencial') })
        .finally(() => { this.savingKey = false })
    },
    revokeKey() {
      if (!window.confirm('El middleware dejará de poder reportar. ¿Revocar la credencial?')) return
      this.savingKey = true
      axios.delete(`${API_BASE_URL}/Parking/building-access/middleware-key/`, { headers: this.authHeaders() })
        .then(() => {
          this.newKey = ''
          this.notify('Credencial revocada')
          return this.fetchMiddleware()
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible revocar la credencial') })
        .finally(() => { this.savingKey = false })
    },
    copyKey() {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(this.newKey).then(() => this.notify('API key copiada'))
      }
    }
  }
}
</script>

<style scoped>
.facial-devices {
  padding: 20px;
}
.device-card {
  cursor: pointer;
}
.device-inactive {
  opacity: 0.55;
}
.device-svg {
  width: 90px;
  height: 150px;
}
.device-name,
.device-location {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.detail-card {
  overflow: hidden;
}
.detail-header {
  position: relative;
  padding: 20px 24px;
}
.detail-close {
  position: absolute;
  top: 6px;
  right: 6px;
}
.detail-header-content {
  display: flex;
  align-items: center;
}
.detail-illustration {
  width: 64px;
  height: 108px;
  flex-shrink: 0;
  margin-right: 20px;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.35));
}
.detail-title {
  min-width: 0;
}
.detail-location {
  margin: 2px 0 8px;
  opacity: 0.9;
}
.detail-hint {
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.9;
}
.detail-body {
  padding-top: 16px;
}
.section-title {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #78909c;
}
.info-tile {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f5f7f8;
}
.info-icon {
  margin-right: 12px;
}
.info-text {
  min-width: 0;
}
.info-value {
  word-break: break-word;
}
.notes-box {
  margin-top: 12px;
  padding: 10px 12px;
  border-left: 3px solid #90a4ae;
  background: #fafafa;
  font-size: 14px;
}
.access-summary {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.access-list {
  padding: 0;
}
.access-time {
  align-items: flex-end;
  min-width: 56px;
}
.access-time span {
  display: block;
  text-align: right;
}
.empty-state {
  padding: 32px 0;
  text-align: center;
}
.detail-actions {
  padding: 8px 12px;
}
</style>
